import React, { useMemo, useState } from 'react';
import { ScrollView, View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import Footer from '../components/Footer';

const currency = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' });

const numberFromInput = (v) => {
  const n = parseFloat(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};

export default function BrokerageCalculatorScreen() {
  const [segment, setSegment] = useState('Delivery'); // 'Delivery' | 'Intraday'
  const [quantity, setQuantity] = useState('100');
  const [buyPrice, setBuyPrice] = useState('100');
  const [sellPrice, setSellPrice] = useState('105');

  const [brokerageType, setBrokerageType] = useState('Percentage'); // 'Percentage' | 'Flat'
  const [brokerageRate, setBrokerageRate] = useState('0.03'); // percent or flat per order in INR

  const [sttRate, setSttRate] = useState('0.10'); // %
  const [exchangeRate, setExchangeRate] = useState('0.00325'); // % of turnover
  const [gstRate, setGstRate] = useState('18'); // % on (brokerage + exchange)
  const [sebiRate, setSebiRate] = useState('0.0001'); // % of turnover
  const [stampRate, setStampRate] = useState('0.015'); // % on buy side

  const data = useMemo(() => {
    const qty = numberFromInput(quantity);
    const bp = numberFromInput(buyPrice);
    const sp = numberFromInput(sellPrice);
    const turnBuy = qty * bp;
    const turnSell = qty * sp;
    const turnover = turnBuy + turnSell;

    // Brokerage
    let brokerageBuy = 0;
    let brokerageSell = 0;
    if (brokerageType === 'Percentage') {
      brokerageBuy = (numberFromInput(brokerageRate) / 100) * turnBuy;
      brokerageSell = (numberFromInput(brokerageRate) / 100) * turnSell;
    } else {
      brokerageBuy = numberFromInput(brokerageRate);
      brokerageSell = numberFromInput(brokerageRate);
    }
    const brokerageTotal = brokerageBuy + brokerageSell;

    // STT
    const sttR = numberFromInput(sttRate) / 100;
    let sttBuy = 0;
    let sttSell = 0;
    if (segment === 'Delivery') {
      sttBuy = sttR * turnBuy;
      sttSell = sttR * turnSell;
    } else {
      // Intraday: commonly applied on sell side only
      sttSell = sttR * turnSell;
    }
    const sttTotal = sttBuy + sttSell;

    // Exchange Transaction Charges
    const exch = (numberFromInput(exchangeRate) / 100) * turnover;

    // SEBI Charges
    const sebi = (numberFromInput(sebiRate) / 100) * turnover;

    // Stamp Duty (buy side)
    const stamp = (numberFromInput(stampRate) / 100) * turnBuy;

    // GST (@ on brokerage + exchange)
    const gst = (numberFromInput(gstRate) / 100) * (brokerageTotal + exch);

    const totalCharges = brokerageTotal + sttTotal + exch + sebi + stamp + gst;
    const grossPnL = turnSell - turnBuy;
    const netPnL = grossPnL - totalCharges;

    return {
      qty,
      bp,
      sp,
      turnBuy,
      turnSell,
      turnover,
      brokerageBuy,
      brokerageSell,
      brokerageTotal,
      sttBuy,
      sttSell,
      sttTotal,
      exch,
      sebi,
      stamp,
      gst,
      totalCharges,
      grossPnL,
      netPnL,
    };
  }, [segment, quantity, buyPrice, sellPrice, brokerageType, brokerageRate, sttRate, exchangeRate, gstRate, sebiRate, stampRate]);

  const setDefaultsForSegment = (seg) => {
    setSegment(seg);
    if (seg === 'Delivery') {
      setSttRate('0.10');
      setStampRate('0.015');
    } else {
      setSttRate('0.025');
      setStampRate('0.003');
    }
  };

  const resetAll = () => {
    setDefaultsForSegment('Delivery');
    setQuantity('100');
    setBuyPrice('100');
    setSellPrice('105');
    setBrokerageType('Percentage');
    setBrokerageRate('0.03');
    setExchangeRate('0.00325');
    setGstRate('18');
    setSebiRate('0.0001');
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.heroSection}>
        <View style={styles.heroContent}>
          <Text style={styles.heroTitle}>Brokerage Calculator</Text>
          <Text style={styles.heroSubtitle}>Estimate trading charges and net PnL</Text>
        </View>
      </View>

      <View style={styles.calculatorContainer}>
        <View style={styles.calculatorRow}>
          <View style={styles.leftPanel}>
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Trade Inputs</Text>
              <View style={styles.row}>
                <TouchableOpacity
                  style={[styles.toggle, segment === 'Delivery' && styles.toggleActive]}
                  onPress={() => setDefaultsForSegment('Delivery')}
                >
                  <Text style={[styles.toggleText, segment === 'Delivery' && styles.toggleTextActive]}>Delivery</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.toggle, segment === 'Intraday' && styles.toggleActive]}
                  onPress={() => setDefaultsForSegment('Intraday')}
                >
                  <Text style={[styles.toggleText, segment === 'Intraday' && styles.toggleTextActive]}>Intraday</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Quantity</Text>
                <TextInput value={quantity} onChangeText={setQuantity} style={styles.input} keyboardType="numeric" placeholder="e.g. 100" />
              </View>

              <View style={styles.row}> 
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>Buy Price (₹)</Text>
                  <TextInput value={buyPrice} onChangeText={setBuyPrice} style={styles.input} keyboardType="numeric" placeholder="e.g. 100" />
                </View>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>Sell Price (₹)</Text>
                  <TextInput value={sellPrice} onChangeText={setSellPrice} style={styles.input} keyboardType="numeric" placeholder="e.g. 105" />
                </View>
              </View>
            </View>

            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Charges</Text>
              <View style={styles.row}>
                <TouchableOpacity
                  style={[styles.toggle, brokerageType === 'Percentage' && styles.toggleActive]}
                  onPress={() => setBrokerageType('Percentage')}
                >
                  <Text style={[styles.toggleText, brokerageType === 'Percentage' && styles.toggleTextActive]}>Percentage</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.toggle, brokerageType === 'Flat' && styles.toggleActive]}
                  onPress={() => setBrokerageType('Flat')}
                >
                  <Text style={[styles.toggleText, brokerageType === 'Flat' && styles.toggleTextActive]}>Flat per order</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.row}>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>{brokerageType === 'Percentage' ? 'Brokerage Rate (%)' : 'Brokerage (₹ per leg)'}
                  </Text>
                  <TextInput value={brokerageRate} onChangeText={setBrokerageRate} style={styles.input} keyboardType="numeric" placeholder={brokerageType === 'Percentage' ? 'e.g. 0.03' : 'e.g. 20'} />
                </View>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>STT Rate (%)</Text>
                  <TextInput value={sttRate} onChangeText={setSttRate} style={styles.input} keyboardType="numeric" />
                </View>
              </View>

              <View style={styles.row}>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>Exchange Charges (%)</Text>
                  <TextInput value={exchangeRate} onChangeText={setExchangeRate} style={styles.input} keyboardType="numeric" />
                </View>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>GST (%)</Text>
                  <TextInput value={gstRate} onChangeText={setGstRate} style={styles.input} keyboardType="numeric" />
                </View>
              </View>

              <View style={styles.row}>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>SEBI Charges (%)</Text>
                  <TextInput value={sebiRate} onChangeText={setSebiRate} style={styles.input} keyboardType="numeric" />
                </View>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>Stamp Duty (%) [buy side]</Text>
                  <TextInput value={stampRate} onChangeText={setStampRate} style={styles.input} keyboardType="numeric" />
                </View>
              </View>
            </View>
          </View>

          <View style={styles.rightPanel}>
            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Charges Breakdown</Text>
                <Text style={styles.panelSubtitle}>Turnover and component-wise charges</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Buy Turnover</Text><Text style={styles.summaryValue}>{currency.format(data.turnBuy)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Sell Turnover</Text><Text style={styles.summaryValue}>{currency.format(data.turnSell)}</Text></View>
              <View style={styles.divider} />
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Brokerage (Buy + Sell)</Text><Text style={styles.summaryValue}>{currency.format(data.brokerageTotal)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>STT</Text><Text style={styles.summaryValue}>{currency.format(data.sttTotal)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Exchange Txn Charges</Text><Text style={styles.summaryValue}>{currency.format(data.exch)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>SEBI Charges</Text><Text style={styles.summaryValue}>{currency.format(data.sebi)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Stamp Duty</Text><Text style={styles.summaryValue}>{currency.format(data.stamp)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>GST</Text><Text style={styles.summaryValue}>{currency.format(data.gst)}</Text></View>
            </View>

            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>PnL Summary</Text>
                <Text style={styles.panelSubtitle}>Gross and net after charges</Text>
              </View>
              <View style={styles.summaryRow}><Text style={[styles.summaryLabel, styles.bold]}>Total Charges</Text><Text style={[styles.summaryValue, styles.bold]}>{currency.format(data.totalCharges)}</Text></View>
              <View style={styles.summaryRow}><Text style={[styles.summaryLabel, styles.bold]}>Gross PnL</Text><Text style={[styles.summaryValue, styles.bold]}>{currency.format(data.grossPnL)}</Text></View>
              <View style={styles.summaryRow}><Text style={[styles.summaryLabel, styles.bold]}>Net PnL</Text><Text style={[styles.summaryValue, styles.bold]}>{currency.format(data.netPnL)}</Text></View>
              <View style={{ marginTop: 12, flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity style={styles.buttonPrimary} onPress={resetAll}><Text style={styles.buttonText}>Reset</Text></TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>

      {renderFAQ()}
      {renderSEO()}
      <Footer />
    </ScrollView>
  );
}

function renderFAQ() {
  return (
    <View style={styles.infoSection}>
      <Text style={styles.sectionTitle}>Brokerage Calculator FAQs</Text>
      <Text style={styles.faqQ}>What charges are included in this calculator?</Text>
      <Text style={styles.faqA}>It estimates brokerage, STT, exchange transaction charges, SEBI turnover fees, stamp duty, and GST. Values are configurable so you can match your broker and segment.</Text>

      <Text style={styles.faqQ}>Is STT applied on both buy and sell?</Text>
      <Text style={styles.faqA}>For equity delivery, STT is commonly applied on both sides. For intraday, STT typically applies only on the sell side. You can change the rate as needed.</Text>

      <Text style={styles.faqQ}>What does flat brokerage mean?</Text>
      <Text style={styles.faqA}>Flat brokerage charges a fixed amount per executed order (per leg). Discount brokers often use flat brokerage for intraday and F&O segments.</Text>

      <Text style={styles.faqQ}>How accurate are the default rates?</Text>
      <Text style={styles.faqA}>Defaults reflect commonly referenced values. Actual rates vary by broker, exchange, and regulation. Always confirm with your broker’s pricing and state stamp duty.</Text>
    </View>
  );
}

function renderSEO() {
  return (
    <View style={styles.seoSection}>
      <Text style={styles.seoTitle}>Brokerage Calculator: Charges Breakdown and Net PnL</Text>
      <Text style={styles.sectionText}>
        Use our Brokerage Calculator to estimate total trading costs for equity delivery or intraday trades in India. Configure brokerage as percentage or flat per order, and adjust STT, exchange transaction charges, GST, SEBI fees, and stamp duty to match your broker’s schedule. The calculator shows a transparent breakdown and computes net profit or loss after all charges.
      </Text>
      <Text style={styles.sectionText}>
        For long-term investing, explore our SIP and CAGR tools; for short-term planning, compare EMI methods with the Flat vs Reducing Rate calculator. Accurate cost estimation helps avoid surprises and improves execution decisions.
      </Text>
      <Text style={styles.disclaimer}>Disclaimer: This tool provides estimates for educational purposes. Brokerage and regulatory charges vary by broker, exchange, and state. Please verify actual fees before trading.</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  content: { paddingBottom: 32 },
  heroSection: { backgroundColor: '#4F46E5', paddingVertical: 24, paddingHorizontal: 30 },
  heroContent: { width: '100%', alignSelf: 'stretch' },
  heroTitle: { fontSize: 28, fontWeight: 'bold', color: '#ffffff', marginBottom: 6 },
  heroSubtitle: { fontSize: 14, color: '#e0e7ff' },

  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 16, paddingVertical: 12 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  leftPanel: { flex: 0.6 },
  rightPanel: { flex: 0.4 },

  card: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, marginHorizontal: 16, borderWidth: 1, borderColor: '#e5e7eb' },
  infoSection: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, marginHorizontal:30, borderWidth: 1, borderColor: '#e5e7eb' },
  seoSection: { backgroundColor: '#ffffff', borderRadius: 12, marginHorizontal:30, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  seoTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  sectionTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 12, marginHorizontal: 16 },
  panelHeader: { alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b', marginTop: 2 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  inputGroup: { marginBottom: 12 },
  label: { color: '#475569', marginBottom: 6 },
  input: { backgroundColor: '#ffffff', borderColor: '#e5e7eb', borderWidth: 1, borderRadius: 8, color: '#0f172a', paddingHorizontal: 12, paddingVertical: 10 },
  flexItem: { flex: 1 },
  toggle: { flex: 1, borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, paddingVertical: 10, backgroundColor: '#ffffff' },
  toggleActive: { backgroundColor: '#EBF2FF', borderColor: '#3B82F6' },
  toggleText: { color: '#334155', textAlign: 'center', fontWeight: '600' },
  toggleTextActive: { color: '#1f2937' },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', marginVertical: 6, marginHorizontal: 16 },
  summaryLabel: { color: '#475569' },
  summaryValue: { color: '#0f172a', fontWeight: '600' },
  divider: { height: 1, backgroundColor: '#e5e7eb', marginVertical: 8, marginHorizontal: 16 },
  bold: { fontWeight: '700' },
  buttonPrimary: { backgroundColor: '#3B82F6', paddingVertical: 10, paddingHorizontal: 16, borderRadius: 8, marginLeft: 16 },
  buttonText: { color: '#FFFFFF', fontWeight: '600' },
  faqQ: { color: '#0f172a', fontWeight: '600', marginTop: 8, marginHorizontal: 16 },
  faqA: { color: '#475569', marginTop: 4, marginHorizontal: 16 },
  sectionText: { color: '#475569', marginTop: 6, lineHeight: 20 },
  disclaimer: { color: '#64748b', marginTop: 8, fontSize: 12 },
});